import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ServiceDetails.css";

const ServiceDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Refs for interactions
  const canvasRef = useRef(null);
  const imageCardRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const scrollElementsRef = useRef([]);

  // ================= NOTIFICATION STATE =================
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Fallback data if no state is passed
  const [serviceData, setServiceData] = useState(state || {
    title: "Cloud Infrastructure",
    longDesc: "Transform your legacy systems into agile, cloud-native powerhouses. Our comprehensive modernization strategy begins with a deep assessment of your current architecture. We identify bottlenecks, security vulnerabilities, and scalability limits.\n\nLeveraging leading platforms like AWS and Azure, we redesign your infrastructure to be resilient and cost-efficient.",
    image: "https://picsum.photos/seed/techcloud/800/1000.jpg",
    id: "default-001"
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ================= 1. CANVAS PARTICLE ANIMATION =================
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width, height;
    let particles = [];
    const particleCount = 60;
    const connectionDistance = 150;
    const mouseDistance = 200;
    let animationFrameId;

    const resize = () => {
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
    };

    const mouse = { x: null, y: null };
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;

        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < mouseDistance) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const force = (mouseDistance - distance) / mouseDistance;
            const directionX = forceDirectionX * force * 0.5;
            const directionY = forceDirectionY * force * 0.5;
            this.vx -= directionX;
            this.vy -= directionY;
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 160, 255, 0.5)";
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();

        for (let j = i; j < particles.length; j++) {
          let dx = particles[i].x - particles[j].x;
          let dy = particles[i].y - particles[j].y;
          let distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 160, 255, ${1 - distance / connectionDistance})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener("resize", resize);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);
    
    resize();
    initParticles();
    animate();

    return () => {
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // ================= 2. SCROLL REVEAL ANIMATION =================
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
        }
      });
    }, { threshold: 0.15 });

    const elements = document.querySelectorAll(".reveal-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // ================= 3. 3D TILT EFFECT =================
  useEffect(() => {
    const card = imageCardRef.current;
    const wrapper = imageWrapperRef.current;

    if (!card || !wrapper) return;

    const handleMouseMove = (e) => {
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = ((y - centerY) / centerY) * -8; 
      const rotateY = ((x - centerX) / centerX) * 8; 

      card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = `rotateX(0) rotateY(0) scale(1)`;
    };

    wrapper.addEventListener("mousemove", handleMouseMove);
    wrapper.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      wrapper.removeEventListener("mousemove", handleMouseMove);
      wrapper.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  // ================= 4. TOAST / NOTIFICATION LOGIC =================
  const showNotification = (message) => {
    setToast({ show: true, message: message, type: "success" });
    
    // Hide after 3 seconds
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  const handleNavigate = (path, payload = {}) => {
    showNotification(`Navigating to ${path}...`);
    navigate(path, { state: payload });
  };

  if (!serviceData) return null;

  return (
    <div className="service-details-page">
      {/* ===== TOP BANNER ===== */}
      <div className="service-banner">
        <canvas id="hero-canvas" ref={canvasRef}></canvas>
        <div className="banner-content">
          <div className="breadcrumb">
            <span onClick={() => handleNavigate("/")}>Home</span>
            <span className="slash">/</span>
            <span onClick={() => handleNavigate("/services")}>Services</span>
            <span className="slash">/</span>
            <span className="active">{serviceData.title}</span>
          </div>
        </div>
      </div>

      {/* ===== SPLIT LAYOUT ===== */}
      <section className="details-split">
        {/* LEFT : IMAGE WITH 3D TILT */}
        {serviceData.image && (
          <div className="details-image-wrapper reveal-on-scroll" ref={imageWrapperRef}>
            <div className="details-image-card" ref={imageCardRef}>
              <img src={serviceData.image} alt={serviceData.title} />
              <div className="image-badge">
                <span>Premium Solution</span>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT : CONTENT */}
        <div className="details-text reveal-on-scroll">
          <div className="details-pill">Service Overview</div>

          <h2 className="details-title">
            Strategy with Purpose,<br />Solutions with Impact
          </h2>

          <div className="feature-list">
            <div className="feature-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Scalable Architecture
            </div>
            <div className="feature-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              24/7 Support
            </div>
            <div className="feature-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Cloud Native
            </div>
            <div className="feature-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              Secure & Encrypted
            </div>
          </div>

          <p className="details-description">{serviceData.longDesc}</p>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="service-cta reveal-on-scroll">
        <div className="cta-container">
          <div className="cta-icon-wrapper">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
          </div>
          <h2 className="cta-title">Questions? We’ll put you on the right path.</h2>
          <p className="cta-text">
            Ask about our IT services, technology solutions, implementation approach,
            or pricing. At Vijay Software Solutions, our experienced team is ready to
            understand your business needs and deliver reliable, scalable, and
            future-ready solutions that drive real impact.
          </p>

          <div className="cta-actions">
            <button
              className="cta-button primary"
              onClick={() => handleNavigate("/contact")}
            >
              Get in Touch
            </button>

            <button
              className="cta-button secondary"
              onClick={() => handleNavigate("/checkout", { trainingId: serviceData.id })}
            >
              Editions & Pricing
            </button>
          </div>
        </div>
      </section>

      {/* ===== CUSTOM NOTIFICATION TOAST ===== */}
      <div className={`notification-toast ${toast.show ? "toast-show" : ""}`}>
        <div className="toast-content">
          <div className="toast-icon">✓</div>
          <span className="toast-message">{toast.message}</span>
        </div>
      </div>

    </div>
  );
};

export default ServiceDetails;